import {
  BloodGroup,
  type ErrorMessage,
  type InsuranceProvider,
  type InsuranceProviderFormSchema,
  JSONSchemaFieldType,
} from "../../types";
import { genericVisitValidation } from "./shared";

/**
 * ASHIA
 */
const ASHIARegistrationSchema: InsuranceProviderFormSchema = {
  title: "ASHIA Insurance details",
  description: "Form definition for ASHIA insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      // TODO: Migrate this to insuranceNumber like the other insurance
      // providers Requires changing prod db data.
      name: "registrationNumber",
      title: "Registration Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[BHCPF/]*ANHIS/AK/[0-9]{9}$",
      required: true,
    },
    {
      name: "dateIssued",
      title: "Date Issued",
      type: JSONSchemaFieldType.STRING,
      format: "date",
      required: true,
    },
    {
      name: "expiryDate",
      title: "Expiry Date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
      required: true,
    },
  ],
  orderedProperties: [
    {
      // TODO: Migrate this to insuranceNumber like the other insurance
      // providers Requires changing prod db data.
      name: "registrationNumber",
      title: "Registration Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[BHCPF/]*ANHIS/AK/[0-9]{9}$",
      isRequired: true,
    },
    {
      name: "dateIssued",
      title: "Date Issued",
      type: JSONSchemaFieldType.STRING,
      format: "date",
      isRequired: true,
    },
    {
      name: "expiryDate",
      title: "Expiry Date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
      isRequired: true,
    },
  ],
};

const errMessagesAshiaInsuranceRegistration: Record<string, ErrorMessage> = {
  registrationNumber: {
    required: "Please enter a valid registration number",
    pattern: "Registration should be of the format: ANHIS/AK/000000000",
  },
  dateIssued: {
    required: "Please enter a valid issue date",
  },
  expiryDate: {
    required: "Please enter a valid expiry date",
  },
};

const ASHIARegistrationValidatorConfig = {
  errMessages: errMessagesAshiaInsuranceRegistration,
};

/**
 * KADCHMA
 */
const KADCHMARegistrationSchema: InsuranceProviderFormSchema = {
  title: "KADCHMA Insurance details",
  description: "Form definition for KADCHMA insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
  ],
};

const errMessages: Record<string, ErrorMessage> = {
  insuranceNumber: {
    required: "Please enter a valid membership number",
    pattern: "Membership number must be 7 digits",
  },
};

const KADCHMARegistrationValidatorConfig = {
  errMessages,
};

/**
 * BHCPF, same input fields and validation as KADCHMA
 */
const BHCPFRegistrationSchema: InsuranceProviderFormSchema = {
  title: "BHCPF Insurance details",
  description: "Form definition for BHCPF insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
  ],
};

const BHCPFRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number must be 7 digits",
    },
  },
};

/**
 * Private
 */
const PrivateInsuranceNigeriaRegistrationSchema: InsuranceProviderFormSchema = {
  title: "Private Insurance details",
  description: "Form definition for Private insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "schemeName",
      title: "Scheme name",
      type: JSONSchemaFieldType.STRING,
      required: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      required: true,
    },
    {
      name: "expiryDate",
      title: "Expiry Date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
    },
  ],
  orderedProperties: [
    {
      name: "schemeName",
      title: "Scheme name",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
    {
      name: "expiryDate",
      title: "Expiry Date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
    },
  ],
};

const errMessagesPrivateInsuranceRegistration: Record<string, ErrorMessage> = {
  schemeName: {
    required: "Please enter name of the insurance scheme",
  },
  insuranceNumber: {
    required: "Please enter insurance number",
  },
};

const PrivateInsuranceNigeriaRegistrationValidatorConfig = {
  errMessages: errMessagesPrivateInsuranceRegistration,
};

/**
 * FHIS
 */

const FHISRegistrationSchema: InsuranceProviderFormSchema = {
  title: "FHIS Insurance details",
  description: "Form definition for FHIS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "FHIS Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{12}$",
      required: true,
    },
    {
      name: "issuedDate",
      title: "Date issued",
      type: JSONSchemaFieldType.STRING,
      format: "date",
      required: true,
    },
    {
      name: "bloodGroup",
      title: " Blood group",
      type: JSONSchemaFieldType.STRING,
      required: false,
      enum: [
        BloodGroup.A_POSITIVE,
        BloodGroup.A_NEGATIVE,
        BloodGroup.B_POSITIVE,
        BloodGroup.B_NEGATIVE,
        BloodGroup.ZERO_POSITIVE,
        BloodGroup.ZERO_NEGATIVE,
        BloodGroup.AB_POSITIVE,
        BloodGroup.AB_NEGATIVE,
      ],
    },
    {
      name: "nameOfEmployer",
      title: "Name of employer",
      type: JSONSchemaFieldType.STRING,
      required: false,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "FHIS Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{12}$",
      isRequired: true,
    },
    {
      name: "issuedDate",
      title: "Date issued",
      type: JSONSchemaFieldType.STRING,
      format: "date",
      isRequired: true,
    },
    {
      name: "bloodGroup",
      title: " Blood group",
      type: JSONSchemaFieldType.STRING,
      required: false,
      enum: [
        BloodGroup.A_POSITIVE,
        BloodGroup.A_NEGATIVE,
        BloodGroup.B_POSITIVE,
        BloodGroup.B_NEGATIVE,
        BloodGroup.ZERO_POSITIVE,
        BloodGroup.ZERO_NEGATIVE,
        BloodGroup.AB_POSITIVE,
        BloodGroup.AB_NEGATIVE,
      ],
      meta: {
        enumTitles: ["A+", "A-", "B+", "B-", "0+", "0-", "AB+", "AB-"],
      },
    },
    {
      name: "nameOfEmployer",
      title: "Name of employer",
      type: JSONSchemaFieldType.STRING,
      isRequired: false,
    },
  ],
};

const erroMessagesFHISRegistration: Record<string, ErrorMessage> = {
  insuranceNumber: {
    required: "Please enter a valid insurance number",
    pattern: "Membership number must contain 12 alphanumeric characters",
  },
  issuedDate: {
    required: "Please enter a valid date",
  },
};

const FHISRegistrationValidatorConfig = {
  errMessages: erroMessagesFHISRegistration,
};

/**
 * HOBS-COF
 */

const HOBSCOFRegistrationSchema: InsuranceProviderFormSchema = {
  title: "HOBS-COF Insurance details",
  description: "Form definition for HOBS-COF insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{12}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{12}$",
      isRequired: true,
    },
  ],
};

const HOBSCOFRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number must be 12 characters",
    },
  },
};

/**
 * NiCare
 */

const NiCareRegistrationSchema: InsuranceProviderFormSchema = {
  title: "NiCare Insurance details",
  description: "Form definition for NiCare insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z]{6}[0-9]{6}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
  ],
};

const NiCareRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number must be 6 letters followed by 6 numbers",
    },
  },
};

/**
 * NHIS
 */

const NHISRegistrationSchema: InsuranceProviderFormSchema = {
  title: "NHIS Insurance details",
  description: "Form definition for NHIS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[0-9]{8}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[0-9]{8}$",
      isRequired: true,
    },
  ],
};

const NHISRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number must be 8 numbers",
    },
  },
};

/**
 * DSCHS
 */

const DSCHSRegistrationSchema: InsuranceProviderFormSchema = {
  title: "DSCHS Insurance details",
  description: "Form definition for DSCHS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/lNmKHy
      pattern:
        "^(TMP-[0-9A-F]{10})|(DEP-[0-9A-F]{12})|(TDIN-[0-9A-F]-[0-9]{16})$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern:
        "^(TMP-[0-9A-F]{10})|(DEP-[0-9A-F]{12})|(TDIN-[0-9A-F]-[0-9]{16})$",
      isRequired: true,
    },
  ],
};

const DSCHSRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number should begin with TMP-, DEP- or TDIN-",
    },
  },
};

/**
 * ESUHCS
 */
const ESUHCSRegistrationSchema: InsuranceProviderFormSchema = {
  title: "ESUHCS Insurance details",
  description: "Form definition for ESUHCS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [],
  orderedProperties: [],
};

const ESUHCSRegistrationValidatorConfig = {
  errMessages: {},
};

/**
 * BHIS
 */
const BHISRegistrationSchema: InsuranceProviderFormSchema = {
  title: "BHIS Insurance details",
  description: "Form definition for BHIS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/teNmkH/4
      pattern: "^(BSN[0-9]{7}(_[0-9])?|BY?H(S|C)[0-9]{2}[A-Z0-9]{4,6})$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^(BSN[0-9]{7}(_[0-9])?|BY?H(S|C)[0-9]{2}[A-Z0-9]{4,6})$",
      isRequired: true,
    },
  ],
};

const BHISRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern:
        "Please enter a valid BHIS number, e.g. BSN1234567 or BYHS00896F01",
    },
  },
};

/**
 * ORANGHIS
 */
const ORANGHISRegistrationSchema: InsuranceProviderFormSchema = {
  title: "ORANGHIS Insurance details",
  description: "Form definition for ORANGHIS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/RY2jE0/3
      pattern: "^[A-Z]{3}/[0-9]{7}/[0-9]{2}/[A-Z]/[0-9]$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[A-Z]{3}/[0-9]{7}/[0-9]{2}/[A-Z]/[0-9]$",
      isRequired: true,
    },
  ],
};

const ORANGHISRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number is in the form ABC/1234567/89/D/1",
    },
  },
};

/** /
 * CRSHIA
 */
const CRSHIARegistrationSchema: InsuranceProviderFormSchema = {
  title: "CRSHIA Insurance details",
  description: "Form definition for CRSHIA insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/ZumvEk/2
      pattern: "^CRSBF[0-9]{8}-[0-9]$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^CRSBF[0-9]{8}-[0-9]$",
      isRequired: true,
    },
  ],
};

const CRSHIARegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern:
        "Membership number should begin with CRSBF followed by 9 digits e.g. CRSBF12345678-0",
    },
  },
};

/**
 * KSCHS — Kano State Contributory Healthcare Scheme
 */
const KSCHSRegistrationSchema: InsuranceProviderFormSchema = {
  title: "KSCHS Insurance details",
  description: "Form definition for KSCHS insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/IIP56o/1
      pattern: "^KN/[0-9]{8}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^KN/[0-9]{8}$",
      isRequired: true,
    },
  ],
};

const KSCHSRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number should be in the format KN/00000000",
    },
  },
};

/** /
 * CHAI
 */
const CHAIRegistrationSchema: InsuranceProviderFormSchema = {
  title: "CHAI Insurance details",
  description: "Form definition for CHAI insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/4xNdZr/1
      pattern:
        "^(ODCHC|CHAI)-OND\\|ANE\\|[A-Z]{1}\\|[A-Z]{2}\\|[A-Z]{2}\\|\\d{2}\\|\\d{2}\\|\\d{2}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      pattern:
        "^(ODCHC|CHAI)-OND\\|ANE\\|[A-Z]{1}\\|[A-Z]{2}\\|[A-Z]{2}\\|\\d{2}\\|\\d{2}\\|\\d{2}$",
      isRequired: true,
    },
  ],
};

const CHAIRegistrationValidatorConfig = {
  errMessages: {
    insuranceNumber: {
      required: "Please enter a valid membership number",
      pattern: "Membership number is in the form CHAI-OND|ANE|F|DE|LA|12|29|83",
    },
  },
};

export const nigeriaInsuranceSchemas: InsuranceProvider[] = [
  {
    id: "ASHIA",
    insuranceType: "ASHIA",
    name: "ASHIA",
    registrationValidation: {
      formSchema: ASHIARegistrationSchema,
      validatorConfig: ASHIARegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "BHCPF",
    insuranceType: "BHCPF",
    name: "BHCPF",
    registrationValidation: {
      formSchema: BHCPFRegistrationSchema,
      validatorConfig: BHCPFRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "BHIS",
    insuranceType: "BHIS",
    name: "BHIS",
    registrationValidation: {
      formSchema: BHISRegistrationSchema,
      validatorConfig: BHISRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "CHAI",
    insuranceType: "CHAI",
    name: "CHAI",
    registrationValidation: {
      formSchema: CHAIRegistrationSchema,
      validatorConfig: CHAIRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "CRSHIA",
    insuranceType: "CRSHIA",
    name: "CRSHIA",
    registrationValidation: {
      formSchema: CRSHIARegistrationSchema,
      validatorConfig: CRSHIARegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "DSCHS",
    insuranceType: "DSCHS",
    name: "DSCHS",
    registrationValidation: {
      formSchema: DSCHSRegistrationSchema,
      validatorConfig: DSCHSRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "ESUHCS",
    insuranceType: "ESUHCS",
    name: "ESUHCS",
    registrationValidation: {
      formSchema: ESUHCSRegistrationSchema,
      validatorConfig: ESUHCSRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "FHIS",
    insuranceType: "FHIS",
    name: "FHIS",
    registrationValidation: {
      formSchema: FHISRegistrationSchema,
      validatorConfig: FHISRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "HOBS-COF",
    insuranceType: "HOBS-COF",
    name: "HOBS-COF",
    registrationValidation: {
      formSchema: HOBSCOFRegistrationSchema,
      validatorConfig: HOBSCOFRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "KADCHMA",
    insuranceType: "KADCHMA",
    name: "KADCHMA",
    registrationValidation: {
      formSchema: KADCHMARegistrationSchema,
      validatorConfig: KADCHMARegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "KSCHS",
    insuranceType: "KSCHS",
    name: "KSCHS",
    registrationValidation: {
      formSchema: KSCHSRegistrationSchema,
      validatorConfig: KSCHSRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "NHIS",
    insuranceType: "NHIS",
    name: "NHIS",
    registrationValidation: {
      formSchema: NHISRegistrationSchema,
      validatorConfig: NHISRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "NiCare",
    insuranceType: "NiCare",
    name: "NiCare",
    registrationValidation: {
      formSchema: NiCareRegistrationSchema,
      validatorConfig: NiCareRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "ORANGHIS",
    insuranceType: "ORANGHIS",
    name: "ORANGHIS",
    registrationValidation: {
      formSchema: ORANGHISRegistrationSchema,
      validatorConfig: ORANGHISRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "Private",
    insuranceType: "Private",
    name: "Private",
    registrationValidation: {
      formSchema: PrivateInsuranceNigeriaRegistrationSchema,
      validatorConfig: PrivateInsuranceNigeriaRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
];
